import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { ChevronLeft, ChevronRight, ScrollText } from "lucide-react";

const ACTIONS = [
  "", "connexion", "creation_membre", "creation_auto", "modification_membre",
  "modification_profil", "action_groupee_statut", "import_csv", "export_csv",
  "invitation_admin", "modification_admin", "demande_suppression", "deconnexion_tous_appareils",
];

export default function AuditLog() {
  const [data, setData] = useState(null);
  const [action, setAction] = useState("");
  const [entite, setEntite] = useState("");
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    const qs = new URLSearchParams({ page: String(page), page_size: "25" });
    if (action) qs.set("action", action);
    if (entite) qs.set("entite", entite);
    api(`/admin/journal?${qs}`).then(setData).catch(() => {});
  }, [action, entite, page]);

  useEffect(() => { load(); }, [load]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <AppLayout variant="admin">
      <div data-testid="audit-log-page" className="space-y-6 animate-fade-in">
        <div className="flex items-center gap-3">
          <ScrollText className="h-7 w-7 text-cfhtb-primary" />
          <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">Journal d'audit</h1>
        </div>

        <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-cfhtb-heading mb-1">Action</label>
            <select data-testid="audit-filter-action" value={action}
              onChange={(e) => { setAction(e.target.value); setPage(1); }}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light">
              {ACTIONS.map((a) => <option key={a} value={a}>{a || "Toutes"}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-cfhtb-heading mb-1">Entité</label>
            <select data-testid="audit-filter-entity" value={entite}
              onChange={(e) => { setEntite(e.target.value); setPage(1); }}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light">
              <option value="">Toutes</option>
              <option value="person">Membre</option>
              <option value="admin_user">Administrateur</option>
            </select>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="audit-table">
              <thead className="bg-[#F7F7F7] border-b border-slate-200">
                <tr>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-cfhtb-heading px-4 py-3">Date</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-cfhtb-heading px-4 py-3">Action</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-cfhtb-heading px-4 py-3">Entité</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-cfhtb-heading px-4 py-3">Acteur</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-cfhtb-heading px-4 py-3">IP</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-cfhtb-heading px-4 py-3">Détails</th>
                </tr>
              </thead>
              <tbody>
                {!data ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-cfhtb-text">Chargement…</td></tr>
                ) : data.items.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-cfhtb-text" data-testid="audit-empty">Aucune entrée.</td></tr>
                ) : (
                  data.items.map((l) => (
                    <tr key={l.id} className="border-b border-slate-100 hover:bg-cfhtb-bg/50 transition-colors text-sm">
                      <td className="px-4 py-2.5 text-cfhtb-text whitespace-nowrap">
                        {l.date ? new Date(l.date).toLocaleString("fr-FR") : "—"}
                      </td>
                      <td className="px-4 py-2.5 font-medium text-cfhtb-heading">{l.action}</td>
                      <td className="px-4 py-2.5 text-cfhtb-text">{l.entite}{l.entite_id ? ` #${String(l.entite_id).slice(-6)}` : ""}</td>
                      <td className="px-4 py-2.5 text-cfhtb-text">{l.acteur_id ? `#${String(l.acteur_id).slice(-6)}` : "système"}</td>
                      <td className="px-4 py-2.5 text-cfhtb-text">{l.ip || "—"}</td>
                      <td className="px-4 py-2.5 text-xs text-cfhtb-text max-w-[280px] truncate">
                        {l.valeurs_apres ? JSON.stringify(l.valeurs_apres) : "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
            <p className="text-xs text-cfhtb-text" data-testid="audit-total">
              {data ? `${data.total} entrée(s) — page ${page} / ${totalPages}` : ""}
            </p>
            <div className="flex gap-2">
              <button data-testid="audit-prev" disabled={page <= 1} onClick={() => setPage(page - 1)}
                className="p-1.5 rounded-md border border-slate-300 text-cfhtb-text hover:bg-slate-50 disabled:opacity-40">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button data-testid="audit-next" disabled={page >= totalPages} onClick={() => setPage(page + 1)}
                className="p-1.5 rounded-md border border-slate-300 text-cfhtb-text hover:bg-slate-50 disabled:opacity-40">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
